import { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { pick, txt } from "@/lib/format";
import { computeRange, exportCsv, PRESETS, toDateInput } from "./utils";
import { SummaryCards } from "./SummaryCards";
import { ItemsTable } from "./ItemsTable";
import { Panel, TimelineChart } from "./Charts";
import { Behavior, Funnel } from "./Behavior";

function RangePicker({ preset, setPreset, from, setFrom, to, setTo, t }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="an-range">
      <div className="flex gap-1 rounded-full border bg-card p-1">
        {PRESETS.map((p) => (
          <button key={p} type="button" onClick={() => setPreset(p)} aria-pressed={preset === p} data-testid={`an-range-${p}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${preset === p ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{t(`an.r.${p}`)}</button>
        ))}
      </div>
      {preset === "custom" && (
        <div className="flex items-center gap-2">
          <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="h-9 w-auto" data-testid="an-range-from" />
          <span className="text-muted-foreground">–</span>
          <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="h-9 w-auto" data-testid="an-range-to" />
        </div>
      )}
    </div>
  );
}

export default function AnalyticsSection({ t, lang }) {
  const today = toDateInput(new Date());
  const [preset, setPreset] = useState("7d");
  const [from, setFrom] = useState(toDateInput(new Date(Date.now() - 6 * 864e5)));
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [state, setState] = useState("loading");
  const range = useMemo(() => computeRange(preset, from, to), [preset, from, to]);

  const load = useCallback(async () => {
    if (!(range.end > range.start)) return;
    setState("loading");
    try {
      const params = { start: range.start.toISOString(), end: range.end.toISOString(), tz: new Date().getTimezoneOffset() };
      const [a, c] = await Promise.all([api.get("/admin/analytics", { params }), api.get("/admin/categories")]);
      setData(a.data);
      setCats(c.data);
      setState("ready");
    } catch {
      setState("error");
    }
  }, [range]);
  useEffect(() => {
    load();
  }, [load]);

  const catName = useCallback((id) => {
    const c = cats.find((x) => x.id === id) || data?.categories.find((x) => x.id === id);
    return c ? txt(c, "name", lang) : "–";
  }, [cats, data, lang]);
  const itemName = (i) => pick(i, "title", lang).text;
  const empty = data && !data.summary.sessions;

  return (
    <section className="space-y-4" data-testid="analytics-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight"><BarChart3 className="h-5 w-5 text-primary" />{t("an.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("an.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RangePicker preset={preset} setPreset={setPreset} from={from} setFrom={setFrom} to={to} setTo={setTo} t={t} />
          <Button variant="secondary" size="sm" disabled={!data || empty} onClick={() => exportCsv(data, { t, lang, catName, itemName, range })} data-testid="an-export-csv"><Download className="h-4 w-4" />{t("an.export")}</Button>
        </div>
      </div>
      {state === "loading" && !data && <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
      {state === "error" && <p className="rounded-2xl border border-dashed p-8 text-center text-sm text-red-300" data-testid="an-error">{t("an.error")}</p>}
      {data && (
        <div className={`space-y-4 transition-opacity ${state === "loading" ? "opacity-60" : ""}`}>
          {empty && <div className="rounded-2xl border border-dashed p-8 text-center" data-testid="an-empty"><p className="font-semibold">{t("an.emptyTitle")}</p><p className="mt-1 text-sm text-muted-foreground">{t("an.emptyHint")}</p></div>}
          <SummaryCards data={data} t={t} lang={lang} catName={catName} />
          <Panel title={t("an.overTime")} testid="an-timeline"><TimelineChart data={data.timeline} t={t} /></Panel>
          <Funnel funnel={data.funnel} t={t} />
          <ItemsTable items={data.items} t={t} lang={lang} catName={catName} />
          <Behavior data={data} t={t} lang={lang} catName={catName} />
        </div>
      )}
    </section>
  );
}
